import type { InstructorSectionCubit } from '@breakoutlearning/firebase-repository/cubits/InstructorSectionCubit'
import { type Section } from '@breakoutlearning/firebase-repository/models/Section'
import type { UserPromotion } from '@breakoutlearning/firebase-repository/models/UserPromotion'
import type { UserProfileCustomProduct } from '@breakoutlearning/firebase-repository/models/UserProfileCustomProduct'
import type { Product } from '@breakoutlearning/firebase-repository/models/Product'
import type {
  ImpersonationInfo,
  UserProfileRole,
} from '@breakoutlearning/firebase-repository/types'
import {
  OrganizationInvoiceStatus,
  SectionPaymentMethod,
  SuperAdminPrivilege,
} from '@breakoutlearning/firebase-repository/types'
import { noTryAsync } from '@breakoutlearning/firebase-repository/util'
import { AdminOnlyRing } from 'components/AdminOnlyRing'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { BreakoutSwitch } from 'components/design-system/BreakoutSwitch'
import { BreakoutSelect } from 'components/design-system/BreakoutSelect'
import { BreakoutTextInput } from 'components/design-system/BreakoutTextInput'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'
import { FormError } from 'components/design-system/form/FormError'
import { Dialog, InlineDialog } from 'components/dialogs/Dialog'
import { DialogCloseButton } from 'components/dialogs/DialogCloseButton'
import { useDialogs } from 'hooks/dialogs'
import { useBreakoutUser, useImpersonatedBreakoutUser } from 'hooks/profile'
import { observer } from 'mobx-react-lite'
import { useCallback, useMemo, useRef, useState, useEffect, useId } from 'react'
import type { UseFormWatch } from 'react-hook-form'
import { Controller, useForm } from 'react-hook-form'
import { PromotionSelectionWidget } from './PromotionSelectionWidget'
import { useRepository } from 'hooks/auth'
import { useTranslationTyped } from 'i18n/i18n'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { type BreakoutUser } from '@breakoutlearning/firebase-repository/models/BreakoutUser'
import { dateSchema } from 'util/schema-date'
import {
  BreakoutDateTimeInput,
  FixedTimeComponents,
} from 'components/design-system/BreakoutDateTimeInput'
import { TriangleAlertIcon } from 'components/icons/TriangleAlert'
import { DateTime } from 'luxon'
import type { PublicUser } from '@breakoutlearning/firebase-repository/models/PublicUser'
import type { SectionAssignment } from '@breakoutlearning/firebase-repository/models/SectionAssignment'
import { useCubitBuilder } from 'hooks/cubits'
import { CreateOrEditSectionDialogCubit } from '@breakoutlearning/firebase-repository/cubits/CreateOrEditSectionDialogCubit'
import { PricingSelectionWidget } from 'pages/instructor/classes/PricingSelectionWidget'
import { Spinner } from 'components/Spinner'
import { InvoiceRequestDialog } from 'pages/instructor/dialogs/InvoiceRequestDialog'
import type { SectionProduct } from '@breakoutlearning/firebase-repository/models/SectionProduct'
import { WarningBox } from 'components/WarningBox'
import { SectionEndDateConflictDialog } from './SectionEndDateConflictDialog'
import type { OrganizationCustomProduct } from '@breakoutlearning/firebase-repository/models/OrganizationCustomProduct'
import type { Organization } from '@breakoutlearning/firebase-repository/models/Organization'
import { InstructorSelectOption } from 'components/InstructorSelectOption'

const SECTION_AND_CLASS_NAME_MAX_LENGTH = 50

const formSchema = ({
  tt,
  earliestAssignmentStart,
  latestAssignmentExpiry,
  startDateDisabled,
  endDateDisabled,
  requireChoosingInstructor,
  requireProductSelection,
}: {
  tt: ReturnType<typeof useTranslationTyped>['tt']
  earliestAssignmentStart?: DateTime
  latestAssignmentExpiry?: DateTime
  startDateDisabled?: boolean
  endDateDisabled?: boolean
  requireChoosingInstructor: boolean
  requireProductSelection?: boolean
}) =>
  z.object({
    className: z
      .string()
      .trim()
      .min(1, tt.instructor_library.class_name_required())
      .max(
        SECTION_AND_CLASS_NAME_MAX_LENGTH,
        tt.instructor_library.max_characters({
          count: SECTION_AND_CLASS_NAME_MAX_LENGTH,
        })
      ),
    sectionName: z
      .string()
      .trim()
      .min(1, tt.instructor_library.section_name_required())
      .max(
        SECTION_AND_CLASS_NAME_MAX_LENGTH,
        tt.instructor_library.max_characters({
          count: SECTION_AND_CLASS_NAME_MAX_LENGTH,
        })
      ),
    expectedStudents: z.coerce
      .number({
        invalid_type_error: tt.instructor_library.expected_students_required(),
      })
      .min(1, tt.instructor_library.expected_students_required())
      .max(1500, tt.instructor_library.expected_students_required()),
    organizationId: z.string().optional(),
    sectionStartDate: dateSchema({
      required: startDateDisabled
        ? undefined
        : tt.instructor_library.section_start_date_required(),
    }).refine(
      (date) => {
        if (startDateDisabled) return true
        if (!earliestAssignmentStart) return true
        return date <= earliestAssignmentStart
      },
      {
        message: earliestAssignmentStart
          ? tt.instructor_library.section_start_date_after_assignment({
              date: earliestAssignmentStart.toLocaleString(DateTime.DATE_MED),
            })
          : tt.instructor_library.section_start_date_after_assignment(),
      }
    ),
    sectionEndDate: dateSchema({
      required: endDateDisabled
        ? undefined
        : tt.instructor_library.section_end_date_required(),
    }).refine(
      (date) => {
        if (endDateDisabled) return true // skip validation if disabled
        if (!latestAssignmentExpiry) return true // no latest assignment, so always valid
        return date >= latestAssignmentExpiry // end date must be after latest assignment expiry
      },
      {
        message: latestAssignmentExpiry
          ? tt.instructor_library.section_end_date_before_assignment({
              date: latestAssignmentExpiry.toLocaleString(DateTime.DATE_MED),
            })
          : tt.instructor_library.section_end_date_before_assignment(),
      }
    ),
    selectedInstructorId: requireChoosingInstructor
      ? z.string({
          required_error: tt.instructor_library.instructor_required(),
        })
      : z.string().optional(),
    isTest: z.boolean().optional(),
    useCodelessAccess: z.boolean().optional(),
    selectedProduct: z
      .object({
        productId: z.string().optional(),
        sectionPassPrice: z.number(),
        sectionPassQuantity: z.number(),
        sectionPassProductName: z.string().optional(),
        isCustom: z.boolean(),
      })
      .optional()
      .refine((val) => !requireProductSelection || val !== undefined, {
        message: 'Please select a pricing option',
      }),
  })

type FormValues = z.infer<ReturnType<typeof formSchema>>

type CreateOrEditSectionFormProps = {
  inline?: boolean
  open?: boolean
  constrainingAssignments?: SectionAssignment[]
  section?: Section
  sectionCompleted?: boolean
  hasApprovedInvoiceRequest?: boolean
  applyApprovedFreeAdminPromotion: InstructorSectionCubit['applyApprovedFreeAdminPromotion']
  createSectionInvoiceRequest: InstructorSectionCubit['createSectionInvoiceRequest']
  onCancel?: () => void
  onSave?: ({
    sectionId,
    invoiceCreated,
  }: {
    sectionId: string
    invoiceCreated?: boolean
  }) => void
  saveSection: SaveSection
  redeemPromotions: (
    sectionId: string,
    userPromotions: UserPromotion[]
  ) => Promise<void>
  editOrCreate: 'edit' | 'create'
}

export const CreateOrEditSectionDialog = observer(
  function CreateOrEditSectionDialog(props: CreateOrEditSectionFormProps) {
    const user = useImpersonatedBreakoutUser()

    const instructorsToChooseFrom = useMemo(() => {
      const usersIAssistValue = user.usersIAssist.value ?? []
      if (
        (user.isInstructor || user.isInternal) &&
        usersIAssistValue.length > 0
      ) {
        return [...usersIAssistValue, user.publicUser]
      }
      return usersIAssistValue
    }, [
      user.usersIAssist.value,
      user.isInstructor,
      user.isInternal,
      user.publicUser,
    ])

    const { tt } = useTranslationTyped()
    const repository = useRepository()
    const headingId = useId()

    const mostRecentSectionProduct = props.section?.id
      ? user.getMostRecentSectionProduct(props.section.id)
      : undefined

    /** Let's try to use this more in future to prevent needing to pass in data from disparate places: */
    const cubit = useCubitBuilder(
      () =>
        new CreateOrEditSectionDialogCubit(repository, {
          role: user.role as UserProfileRole,
          impersonationInfo: { userId: user.uid },
        }),
      [user.role, user.uid]
    )

    const isLoading =
      user.defaultSystemProducts.isLoading ||
      user.shouldHideDefaultProducts.isLoading ||
      user.userCustomProducts.isLoading ||
      user.isLoading ||
      (mostRecentSectionProduct?.isLoading && props.section?.id) ||
      user.allRelatedInstructors.isLoading ||
      user.usersIAssist.isLoading ||
      user.organizations.isLoading

    const DialogToUse = props.inline ? InlineDialog : Dialog

    return (
      <DialogToUse
        open={props.open}
        testId="add-section-dialog"
        size="xl"
        className="flex flex-col items-center"
        innerClassName="flex flex-col h-full w-[600px]"
        aria-labelledby={headingId}
      >
        <h1
          id={headingId}
          className="mb-1 text-center text-headline-large text-core-on-tertiary"
        >
          {props.section?.id
            ? tt.instructor_library.edit_class()
            : tt.instructor_library.add_class()}
        </h1>
        <div className="mb-4 text-center text-body-large text-on-surface-var">
          {tt.instructor_library.fill_in_class_details()}
        </div>
        <DialogCloseButton
          onClick={() => {
            if (props.onCancel) {
              props.onCancel()
              return false
            }
            return true
          }}
        />
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <CreateOrEditSectionForm
            {...props}
            userCustomProducts={user.userCustomProducts.value.models}
            defaultSystemProducts={user.defaultSystemProducts.value.models}
            mostRecentSectionProduct={mostRecentSectionProduct?.value}
            shouldHideDefaultProducts={
              user.shouldHideDefaultProducts.value ?? false
            }
            instructorsToChooseFrom={instructorsToChooseFrom}
            cubit={cubit}
            organizations={user.organizations.value.models}
          />
        )}
      </DialogToUse>
    )
  }
)

const CreateOrEditSectionForm = observer(function CreateOrEditSectionForm({
  constrainingAssignments: _constrainingAssignments,
  section,
  sectionCompleted,
  hasApprovedInvoiceRequest,
  applyApprovedFreeAdminPromotion,
  createSectionInvoiceRequest,
  saveSection,
  onSave,
  redeemPromotions,
  editOrCreate,
  defaultSystemProducts,
  userCustomProducts,
  shouldHideDefaultProducts,
  mostRecentSectionProduct,
  cubit,
  instructorsToChooseFrom,
  organizations,
  onCancel,
}: CreateOrEditSectionFormProps & {
  defaultSystemProducts: Product[]
  userCustomProducts: UserProfileCustomProduct[]
  mostRecentSectionProduct?: SectionProduct | null
  shouldHideDefaultProducts: boolean
  cubit: CreateOrEditSectionDialogCubit
  instructorsToChooseFrom: PublicUser[]
  organizations: Organization[]
}) {
  const { t, tt } = useTranslationTyped()
  const [error, setError] = useState<string | null>(null)

  const { showDialog, clearAllDialogs } = useDialogs()
  const user = useBreakoutUser()
  const canEditCodelessAccess = user.superAdmin.hasPrivilege(
    SuperAdminPrivilege.canEditCodelessAccess
  )
  const { uid: impersonatedUserId } = useImpersonatedBreakoutUser()
  const repository = useRepository()

  const promotionSelectionRef = useRef<{
    selectedUserPromotions: UserPromotion[]
    applyAdminSpecialPromotion: boolean
    user: BreakoutUser
  }>()

  // don't allow cancelled assignments to constrain section date selection
  const constrainingAssignments = _constrainingAssignments?.filter(
    (assignment) => assignment.isCancelled === false
  )

  // These states are loaded dynamically when the user selects options in the form:
  const [instructorProducts, setInstructorProducts] = useState<
    UserProfileCustomProduct[] | null
  >(null)
  const [instructorUseDefaultProducts, setInstructorUseDefaultProducts] =
    useState<boolean | null>(null)
  const [organizationProducts, setOrganizationProducts] = useState<
    OrganizationCustomProduct[] | null
  >(null)
  const [organizationUseDefaultProducts, setOrganizationUseDefaultProducts] =
    useState<boolean | null>(null)
  const [instructorOrganizations, setInstructorOrganizations] = useState<
    Organization[] | null
  >(null)
  const [productsLoading, setProductsLoading] = useState(false)

  // State for the section end date conflict dialog
  const [showConflictDialog, setShowConflictDialog] = useState(false)
  const [conflictingAssignments, setConflictingAssignments] = useState<
    SectionAssignment[]
  >([])

  const latestAssignmentExpiry = constrainingAssignments?.length
    ? DateTime.max(
        ...constrainingAssignments
          .filter((assignment) => assignment.data.expiresAt)
          .map((assignment) => DateTime.fromJSDate(assignment.data.expiresAt!))
      )
    : undefined

  const earliestAssignmentStart = constrainingAssignments?.length
    ? DateTime.min(
        ...constrainingAssignments
          .filter((assignment) => assignment.data.assignedAt)
          .map((assignment) => DateTime.fromJSDate(assignment.data.assignedAt!))
      )
    : undefined

  const sectionHasStudents =
    (section?.userCount ? section.userCount > 0 : false) ||
    (section?.removedUserCount ? section.removedUserCount > 0 : false)

  // Completed sections are read-only for non-Corre users
  const isReadOnly = !!sectionCompleted && !user.isCorre

  // CORRE users can modify section end date even after students are enrolled
  // LMS-linked sections always have dates disabled regardless of state
  const isLms = !!section?.isLtiCourse
  const startDateDisabled = sectionHasStudents || isLms || !!sectionCompleted
  const endDateDisabled =
    (sectionHasStudents && !user.isCorre) || isLms || isReadOnly

  // we want single source of truth on these values, as product selection is dependent on them
  // and we don't want to have to sync them between useForm and useState
  // so we'll watch them in the form and update the state when they change
  const [selectedInstructorId, _setSelectedInstructorId] = useState(
    instructorsToChooseFrom.length > 0
      ? section?.data.instructorUserId
      : undefined
  )
  const [selectedOrganizationId, _setSelectedOrganizationId] = useState(
    section?.data.organizationId
  )

  const customProductsToUse = useMemo(() => {
    if (selectedOrganizationId) return organizationProducts
    if (selectedInstructorId) return instructorProducts
    return userCustomProducts
  }, [
    instructorProducts,
    organizationProducts,
    selectedInstructorId,
    selectedOrganizationId,
    userCustomProducts,
  ])

  const defaultProductsToUse = useMemo(() => {
    if (selectedOrganizationId)
      return organizationUseDefaultProducts ? defaultSystemProducts : []
    if (selectedInstructorId)
      return instructorUseDefaultProducts ? defaultSystemProducts : []

    return shouldHideDefaultProducts ? [] : defaultSystemProducts
  }, [
    defaultSystemProducts,
    instructorUseDefaultProducts,
    organizationUseDefaultProducts,
    selectedInstructorId,
    selectedOrganizationId,
    shouldHideDefaultProducts,
  ])

  const atLeastOneProduct = useMemo(() => {
    const customProducts = customProductsToUse ?? []
    const defaultProducts = defaultProductsToUse ?? []
    return customProducts.length > 0 || defaultProducts.length > 0
  }, [customProductsToUse, defaultProductsToUse])

  const organizationsToUse = useMemo(() => {
    if (selectedInstructorId && instructorOrganizations) {
      return instructorOrganizations
    }
    return organizations
  }, [selectedInstructorId, instructorOrganizations, organizations])

  const isCompletedPayByAssignment = !!(
    sectionCompleted &&
    section &&
    !section.usesSectionPass
  )

  const showProductSelectionUI = useMemo(() => {
    if (isCompletedPayByAssignment) return false
    return atLeastOneProduct
    // this was the original logic, but in LTI context, we always want to require selection
    // && !(section !== undefined && !section.usesSectionPass)
  }, [atLeastOneProduct, isCompletedPayByAssignment])

  const isOrgCodelessAccessEnabled = useMemo(() => {
    if (!selectedOrganizationId) return false
    const selectedOrg = organizations.find(
      (org) => org.id === selectedOrganizationId
    )
    return selectedOrg?.data.defaultCodelessAccess ?? false
  }, [selectedOrganizationId, organizations])

  const {
    control,
    formState: { isSubmitting },
    handleSubmit,
    watch,
    getValues,
    setValue,
  } = useForm<FormValues>({
    resolver: zodResolver(
      formSchema({
        tt,
        requireChoosingInstructor:
          instructorsToChooseFrom && instructorsToChooseFrom.length > 0,
        earliestAssignmentStart,
        latestAssignmentExpiry,
        startDateDisabled,
        endDateDisabled,
        requireProductSelection: showProductSelectionUI,
      })
    ),
    defaultValues: {
      className: section?.data.className ?? '',
      sectionName: section?.data.sectionName ?? '',
      expectedStudents: section?.data.expectedStudents,
      organizationId: section?.data.organizationId,
      sectionStartDate: section?.data.sectionStartDate
        ? DateTime.fromJSDate(section.data.sectionStartDate)
        : editOrCreate === 'create'
          ? DateTime.now()
          : undefined,
      sectionEndDate: section?.data.sectionEndDate
        ? DateTime.fromJSDate(section.data.sectionEndDate)
        : undefined,
      isTest: section?.data.isTest ?? false,
      useCodelessAccess: section?.usesCodelessAccess ?? false,
      selectedProduct: undefined,
      selectedInstructorId:
        instructorsToChooseFrom.length > 0
          ? section?.data.instructorUserId
          : undefined,
    },
  })

  const _selectedInstructorId = watch('selectedInstructorId')
  const _selectedOrganizationId = watch('organizationId')
  const sectionStartDate = watch('sectionStartDate')
  const sectionEndDate = watch('sectionEndDate')

  // sync state values defined before form initialization with form updates
  useEffect(() => {
    if (_selectedInstructorId !== selectedInstructorId)
      _setSelectedInstructorId(_selectedInstructorId)

    if (_selectedOrganizationId !== selectedOrganizationId) {
      _setSelectedOrganizationId(_selectedOrganizationId)
      // Sync codeless access toggle with the new org's default
      if (_selectedOrganizationId) {
        const org = organizations.find((o) => o.id === _selectedOrganizationId)
        setValue('useCodelessAccess', org?.data.defaultCodelessAccess ?? false)
      } else {
        setValue('useCodelessAccess', false)
      }
    }
  }, [
    _selectedInstructorId,
    _selectedOrganizationId,
    selectedInstructorId,
    selectedOrganizationId,
    organizations,
    setValue,
  ])

  const { sectionPaymentMethod, sectionPassPrice, sectionPassQuantity } =
    section?.data ?? {}

  const existingValidSectionPass = useMemo(
    () =>
      section &&
      sectionPaymentMethod !== undefined &&
      [
        SectionPaymentMethod.sectionPassStripe,
        SectionPaymentMethod.sectionPassCoupon,
        SectionPaymentMethod.sectionPassStripeCoupon,
        SectionPaymentMethod.sectionPassCodelessAccess,
      ].includes(sectionPaymentMethod) &&
      sectionPassPrice &&
      sectionPassQuantity
        ? { sectionPassPrice, sectionPassQuantity }
        : null,
    [section, sectionPaymentMethod, sectionPassPrice, sectionPassQuantity]
  )

  const productMatches = useCallback(
    (
      product: UserProfileCustomProduct | OrganizationCustomProduct | Product
    ) => {
      return (
        product.data.sectionPassPrice ===
          existingValidSectionPass?.sectionPassPrice &&
        product.data.sectionPassQuantity ===
          existingValidSectionPass?.sectionPassQuantity
      )
    },
    [existingValidSectionPass]
  )
  const matchingDefaultProduct = useMemo(() => {
    if (!existingValidSectionPass) return undefined

    return defaultProductsToUse?.find(productMatches)
  }, [defaultProductsToUse, productMatches, existingValidSectionPass])

  const matchingCustomProduct = useMemo(() => {
    if (!existingValidSectionPass) return undefined
    return customProductsToUse?.find(productMatches)
  }, [customProductsToUse, productMatches, existingValidSectionPass])

  const legacyProductOption = useMemo(
    () =>
      existingValidSectionPass &&
      !matchingCustomProduct &&
      !matchingDefaultProduct
        ? {
            sectionPassPrice: existingValidSectionPass.sectionPassPrice,
            sectionPassQuantity: existingValidSectionPass.sectionPassQuantity,
            isCustom: true as const,
          }
        : undefined,
    [matchingCustomProduct, matchingDefaultProduct, existingValidSectionPass]
  )

  const defaultSelectedProduct = useMemo(() => {
    if (legacyProductOption) {
      return legacyProductOption
    }

    if (matchingDefaultProduct) {
      return {
        productId: matchingDefaultProduct.id,
        isCustom: false,
        sectionPassPrice: matchingDefaultProduct.data.sectionPassPrice,
        sectionPassQuantity: matchingDefaultProduct.data.sectionPassQuantity,
      }
    }

    if (matchingCustomProduct) {
      return {
        productId: matchingCustomProduct.id,
        isCustom: true,
        sectionPassPrice: matchingCustomProduct.data.sectionPassPrice,
        sectionPassQuantity: matchingCustomProduct.data.sectionPassQuantity,
      }
    }

    if (
      customProductsToUse?.length === 1 &&
      defaultProductsToUse?.length === 0
    ) {
      return {
        productId: customProductsToUse[0].id,
        isCustom: true,
        sectionPassPrice: customProductsToUse[0].data.sectionPassPrice,
        sectionPassQuantity: customProductsToUse[0].data.sectionPassQuantity,
      }
    }

    if (
      defaultProductsToUse?.length === 1 &&
      customProductsToUse?.length === 0
    ) {
      return {
        productId: defaultProductsToUse[0].id,
        isCustom: false,
        sectionPassPrice: defaultProductsToUse[0].data.sectionPassPrice,
        sectionPassQuantity: defaultProductsToUse[0].data.sectionPassQuantity,
      }
    }
  }, [
    customProductsToUse,
    defaultProductsToUse,
    legacyProductOption,
    matchingCustomProduct,
    matchingDefaultProduct,
  ])

  useEffect(() => {
    setValue('selectedProduct', defaultSelectedProduct) // Changes to the instructor or organization will refresh the options for selecting products, so we need to reset the value
  }, [
    customProductsToUse,
    defaultProductsToUse,
    defaultSelectedProduct,
    setValue,
  ])

  // If the most recent user action (SectionProduct record) for this section doesn't match the current section pricing, this implies it has been otherwise set by the system.
  const hasCorreOverride = useMemo(() => {
    if (!mostRecentSectionProduct) return false

    const matchingProduct = [
      ...(customProductsToUse ?? []),
      ...(defaultProductsToUse ?? []),
    ].find((product) => product.id === mostRecentSectionProduct.id)

    if (legacyProductOption && !matchingProduct) return true

    return false
  }, [
    customProductsToUse,
    defaultProductsToUse,
    legacyProductOption,
    mostRecentSectionProduct,
  ])

  useEffect(() => {
    const fetchInstructorProducts = async (instructorId: string) => {
      if (!instructorId) return

      try {
        setProductsLoading(true)
        const [products, hideDefaultProducts] = await Promise.all([
          user.fetchInstructorCustomProducts(instructorId),
          user.fetchInstructorShouldHideDefaultProducts(instructorId),
        ])
        setInstructorUseDefaultProducts(!hideDefaultProducts)
        setInstructorProducts(products ?? [])
      } catch (error) {
        console.error('Error fetching instructor products:', error)
        setInstructorProducts(null)
      } finally {
        setProductsLoading(false)
      }
    }

    if (selectedInstructorId) {
      fetchInstructorProducts(selectedInstructorId)
    } else {
      setInstructorProducts(null)
    }
  }, [selectedInstructorId, user])

  useEffect(() => {
    const fetchOrgProducts = async (organizationId: string) => {
      if (!organizationId) return

      try {
        setProductsLoading(true)
        const [products, hideDefaultProducts] = await Promise.all([
          user.fetchOrganizationCustomProducts({
            organizationId,
            authorizingInstructorId:
              selectedInstructorId !== impersonatedUserId
                ? selectedInstructorId
                : undefined,
          }),
          user.fetchOrganizationHideDefaultProducts(organizationId),
        ])
        setOrganizationUseDefaultProducts(!hideDefaultProducts)
        setOrganizationProducts(products ?? [])
      } catch (error) {
        console.error('Error fetching organization products:', error)
        setOrganizationProducts(null)
        setOrganizationUseDefaultProducts(null)
      } finally {
        setProductsLoading(false)
      }
    }
    if (selectedOrganizationId) {
      fetchOrgProducts(selectedOrganizationId)
    } else {
      setOrganizationProducts(null)
    }
  }, [
    selectedOrganizationId,
    repository,
    user,
    selectedInstructorId,
    impersonatedUserId,
  ])

  useEffect(() => {
    const fetchInstructorOrganizations = async (instructorId: string) => {
      if (!instructorId) return

      try {
        const instructorOrgs =
          await user.fetchOrganizationsByUserId(instructorId)
        setInstructorOrganizations(instructorOrgs)

        // If the currently selected organization is not in the new instructor's orgs, reset it
        if (
          selectedOrganizationId &&
          !instructorOrgs.some((org) => org.id === selectedOrganizationId)
        ) {
          setValue('organizationId', undefined)
        }
      } catch (error) {
        console.error('Error fetching instructor organizations:', error)
        setInstructorOrganizations(null)
      }
    }

    if (selectedInstructorId) {
      fetchInstructorOrganizations(selectedInstructorId)
    } else {
      setInstructorOrganizations(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInstructorId])

  // when selecting end date, default view to 2 months out from start
  // this is the earliest possible date that will not show a warning
  const sectionEndDateInitialView = useMemo(() => {
    const startPoint = sectionStartDate ?? DateTime.now()
    return startPoint.plus({ months: 2 })
  }, [sectionStartDate])

  const submit = useCallback(
    async ({
      className,
      sectionName,
      organizationId,
      expectedStudents,
      sectionStartDate,
      sectionEndDate,
      selectedInstructorId,
      selectedProduct,
      isTest,
      useCodelessAccess,
    }: FormValues) => {
      let productToSave =
        showProductSelectionUI && selectedProduct?.productId
          ? {
              productId: selectedProduct.productId,
              isCustom: selectedProduct.isCustom,
            }
          : undefined

      const priceChanged =
        selectedProduct?.sectionPassPrice !==
        defaultSelectedProduct?.sectionPassPrice

      const quantityChanged =
        selectedProduct?.sectionPassQuantity !==
        defaultSelectedProduct?.sectionPassQuantity

      const productIdChanged = !(
        defaultSelectedProduct &&
        'productId' in defaultSelectedProduct &&
        selectedProduct?.productId === defaultSelectedProduct?.productId
      )

      if (
        existingValidSectionPass &&
        !priceChanged &&
        !quantityChanged &&
        !productIdChanged
      ) {
        productToSave = undefined
      }

      // Check for conflicts if CORRE user is changing section end date on released section
      if (
        user.isCorre &&
        sectionHasStudents &&
        editOrCreate === 'edit' &&
        sectionEndDate &&
        section &&
        constrainingAssignments
      ) {
        const currentEndDate = section.data.sectionEndDate
          ? DateTime.fromJSDate(section.data.sectionEndDate)
          : null

        // Only check for conflicts if the end date is actually changing
        if (!currentEndDate || !sectionEndDate.equals(currentEndDate)) {
          const conflicts = constrainingAssignments.filter((assignment) => {
            if (!assignment.data.expiresAt) return false
            const expiresAt = DateTime.fromJSDate(assignment.data.expiresAt)
            return expiresAt > sectionEndDate
          })

          if (conflicts.length > 0) {
            setConflictingAssignments(conflicts)
            setShowConflictDialog(true)
            return // Don't proceed with save yet
          }
        }
      }

      const [sectionId, err] = await noTryAsync(async () => {
        return await saveSection({
          className,
          sectionName,
          expectedStudents,
          organizationId,
          sectionStartDate: startDateDisabled ? undefined : sectionStartDate,
          sectionEndDate: endDateDisabled ? undefined : sectionEndDate,
          impersonationInfo: selectedInstructorId
            ? {
                userId: selectedInstructorId,
              }
            : { userId: impersonatedUserId },
          selectedProduct: productToSave,
          ...(user.isCorre ? { isTest } : {}),
          // When the org has codeless access enabled, corre can override per-section.
          // Pass token when toggled off so the cubit doesn't re-apply the org default;
          // the Go SectionProduct trigger will upgrade token → sectionPassStripe
          // if a product is selected.
          ...(user.isCorre && isOrgCodelessAccessEnabled
            ? {
                sectionPaymentMethod: useCodelessAccess
                  ? SectionPaymentMethod.sectionPassCodelessAccess
                  : SectionPaymentMethod.token,
              }
            : {}),
        })
      })

      if (err || !sectionId) {
        err && console.error(err)
        const error =
          editOrCreate === 'edit'
            ? tt.instructor_library.error_updating_class()
            : tt.instructor_library.error_adding_class()

        setError(error)
        return
      }

      const hasSelectedUserPromotions =
        promotionSelectionRef.current !== undefined &&
        (promotionSelectionRef.current.selectedUserPromotions.length > 0 ||
          promotionSelectionRef.current.applyAdminSpecialPromotion)

      // apply promotions
      if (promotionSelectionRef.current) {
        const promotionPromises: Promise<void>[] = []
        const {
          selectedUserPromotions,
          applyAdminSpecialPromotion,
          user: promotionUser,
        } = promotionSelectionRef.current

        if (applyAdminSpecialPromotion)
          promotionPromises.push(
            applyApprovedFreeAdminPromotion({
              userId: promotionUser.uid,
              sectionId,
            })
          )

        if (selectedUserPromotions.length) {
          promotionPromises.push(
            redeemPromotions(sectionId, selectedUserPromotions)
          )
        }

        const [, redemptionErr] = await noTryAsync(() =>
          Promise.all(promotionPromises)
        )

        if (redemptionErr) {
          console.error(redemptionErr)
          return setError(t('instructor_library.error_redeeming_promotions'))
        }
      }

      if (editOrCreate === 'create') {
        const organization = organizations.find(
          (org) => org.id === organizationId
        )

        const newSection = await cubit.fetchSection(sectionId)

        if (
          organization &&
          !hasSelectedUserPromotions &&
          organization.data.organizationInvoiceStatus ===
            OrganizationInvoiceStatus.adminApproved
        ) {
          return showDialog(() => (
            <InvoiceRequestDialog
              section={newSection}
              onCreate={() => {
                clearAllDialogs()
                onSave?.({ sectionId, invoiceCreated: true })
              }}
              onSkip={() => {
                clearAllDialogs()
                onSave?.({ sectionId, invoiceCreated: false })
              }}
              createSectionInvoiceRequest={async ({
                sectionRequestAssignmentCount,
                sectionRequestStudentCount,
              }) => {
                await createSectionInvoiceRequest({
                  sectionId,
                  sectionRequest: {
                    sectionRequestAssignmentCount,
                    sectionRequestStudentCount,
                    requestedAt: new Date(),
                    organizationId: organizationId!,
                    sectionId,
                  },
                })
              }}
            />
          ))
        }
      }

      onSave?.({ sectionId, invoiceCreated: false })
    },
    [
      showProductSelectionUI,
      defaultSelectedProduct,
      existingValidSectionPass,
      editOrCreate,
      onSave,
      saveSection,
      startDateDisabled,
      endDateDisabled,
      impersonatedUserId,
      tt.instructor_library,
      applyApprovedFreeAdminPromotion,
      redeemPromotions,
      t,
      organizations,
      cubit,
      showDialog,
      clearAllDialogs,
      createSectionInvoiceRequest,
      user.isCorre,
      isOrgCodelessAccessEnabled,
      sectionHasStudents,
      section,
      constrainingAssignments,
      setConflictingAssignments,
      setShowConflictDialog,
    ]
  )

  const pricingDisabled =
    (section?.userCount && section.userCount > 0) ||
    (section?.removedUserCount && section.removedUserCount > 0)

  const showInstructorChooseFrom =
    instructorsToChooseFrom &&
    instructorsToChooseFrom.length > 0 &&
    !section?.data.instructorUserId

  // Handle conflict dialog actions
  const handleConflictDialogClose = useCallback(() => {
    setShowConflictDialog(false)
    setConflictingAssignments([])
  }, [])

  return (
    <>
      <form
        // disable native validation - we use react-hook-form for that
        noValidate
        className="flex flex-1 flex-col gap-5 pb-4"
        onSubmit={handleSubmit(submit, (invalidFields) => {
          // if the only invalid reason IS the dates issue and the user is CORRE, lets show the dialog here
          if (
            user.isCorre &&
            Object.keys(invalidFields).length === 1 &&
            invalidFields.sectionEndDate
          ) {
            const sectionEndDate = getValues('sectionEndDate')
            if (
              sectionEndDate &&
              constrainingAssignments &&
              constrainingAssignments.length > 0
            ) {
              const conflicts = constrainingAssignments.filter((assignment) => {
                if (!assignment.data.expiresAt) return false
                const expiresAt = DateTime.fromJSDate(assignment.data.expiresAt)
                return expiresAt > sectionEndDate
              })

              if (conflicts.length > 0) {
                setConflictingAssignments(conflicts)
                setShowConflictDialog(true)
                return
              }
            }
          }
        })}
      >
        <div className="flex-1">
          <div className="flex flex-1 flex-col gap-2">
            <AdminOnlyRing
              label={tt.roles.corre_only()}
              onlyShowIf={user.isCorre}
            >
              <Controller
                control={control}
                name="isTest"
                render={({ field }) => (
                  <BreakoutSwitch
                    labelPosition="left"
                    className="w-full justify-between gap-4 pr-3"
                    label={tt.instructor_class.mark_as_test_section()}
                    checked={field.value ?? false}
                    onChange={(e) => field.onChange(e.target.checked)}
                    testId="mark-as-test-section"
                    accent
                  />
                )}
              />
              {isOrgCodelessAccessEnabled && !hasApprovedInvoiceRequest && (
                <Controller
                  control={control}
                  name="useCodelessAccess"
                  render={({ field }) => (
                    <BreakoutSwitch
                      labelPosition="left"
                      className="w-full justify-between gap-4 pr-3"
                      label={tt.organization.codeless_access.default_codeless_access()}
                      tooltip={
                        canEditCodelessAccess
                          ? tt.organization.codeless_access.default_codeless_access_tooltip()
                          : tt.organization.codeless_access.edit_restricted_tooltip()
                      }
                      disabled={!canEditCodelessAccess}
                      checked={field.value ?? false}
                      onChange={(e) => field.onChange(e.target.checked)}
                      testId="use-codeless-access"
                    />
                  )}
                />
              )}
            </AdminOnlyRing>
            {showInstructorChooseFrom && (
              <Controller
                control={control}
                name="selectedInstructorId"
                render={({ field, fieldState }) => (
                  <BreakoutSelect
                    kind="secondary"
                    label={tt.instructor_library.instructor()}
                    disabled={!!sectionCompleted}
                    options={instructorsToChooseFrom.map((user) =>
                      InstructorSelectOption({
                        instructor: user,
                        text: user.fullName,
                        value: user.id,
                      })
                    )}
                    required
                    error={fieldState.error}
                    {...field}
                  />
                )}
              />
            )}

            <div className="flex w-full gap-3">
              <Controller
                control={control}
                name="className"
                rules={{
                  required: tt.instructor_library.class_name_required(),
                }}
                render={({ field, fieldState }) => (
                  <BreakoutTextInput
                    label={t('instructor_library.class_name')}
                    autoFocus
                    data-testid="class-name-input"
                    kind="secondary"
                    className="flex-1"
                    disabled={!!sectionCompleted}
                    error={fieldState.error}
                    maxLength={SECTION_AND_CLASS_NAME_MAX_LENGTH}
                    required
                    {...field}
                  />
                )}
              />

              <Controller
                control={control}
                name="sectionName"
                rules={{
                  required: tt.instructor_library.section_name_required(),
                }}
                render={({ field, fieldState }) => (
                  <BreakoutTextInput
                    label={t('instructor_library.section_name')}
                    data-testid="section-name-input"
                    kind="secondary"
                    className="flex-1"
                    disabled={!!sectionCompleted}
                    error={fieldState.error}
                    maxLength={SECTION_AND_CLASS_NAME_MAX_LENGTH}
                    required
                    {...field}
                  />
                )}
              />
            </div>

            <Controller
              control={control}
              name="expectedStudents"
              render={({ field, fieldState }) => (
                <BreakoutTextInput
                  label={tt.instructor_library.expected_students()}
                  sublabel={tt.instructor_library.estimate_is_fine()}
                  data-testid="expected-students-input"
                  kind="secondary"
                  type="number"
                  disabled={!!sectionCompleted}
                  error={fieldState.error}
                  required
                  {...field}
                />
              )}
            />

            <div className="flex gap-2">
              <div className="flex-1">
                <BreakoutTooltip
                  content={tt.instructor_library.section_dates_warning_4()}
                  enabled={startDateDisabled}
                >
                  <div>
                    <Controller
                      control={control}
                      name="sectionStartDate"
                      render={({ field, fieldState }) => (
                        <BreakoutDateTimeInput
                          label={tt.instructor_library.section_start_date()}
                          disabled={startDateDisabled}
                          data-testid="section-start-date-input"
                          kind="secondary"
                          error={fieldState.error}
                          required={!startDateDisabled}
                          resolution={{
                            interval: '1day',
                            fixedTimeComponent: FixedTimeComponents.midnight,
                          }}
                          min={getValues('sectionStartDate')?.minus({
                            months: 6,
                          })}
                          max={getValues('sectionEndDate')?.minus({
                            days: 1,
                          })}
                          {...field}
                        />
                      )}
                    />
                  </div>
                </BreakoutTooltip>
              </div>
              <div className="flex-1">
                <BreakoutTooltip
                  content={tt.instructor_library.section_dates_warning_4()}
                  enabled={endDateDisabled}
                >
                  <div>
                    <Controller
                      control={control}
                      name="sectionEndDate"
                      render={({ field, fieldState }) => (
                        <BreakoutDateTimeInput
                          label={tt.instructor_library.section_end_date()}
                          disabled={endDateDisabled}
                          data-testid="section-end-date-input"
                          kind="secondary"
                          error={fieldState.error}
                          required={!endDateDisabled}
                          initialView={sectionEndDateInitialView}
                          hideNowButton
                          resolution={{
                            interval: '1day',
                            fixedTimeComponent: FixedTimeComponents.endOfDay,
                          }}
                          min={DateTime.max(
                            DateTime.now(),
                            getValues('sectionStartDate') ?? DateTime.now()
                          )
                            .plus({
                              days: 1,
                            })
                            .startOf('day')}
                          {...field}
                        />
                      )}
                    />
                  </div>
                </BreakoutTooltip>
              </div>
            </div>

            <SectionDatesWarningBox watch={watch} />

            {organizationsToUse.length > 0 && (
              <BreakoutTooltip
                content={t('instructor_library.organization_disabled_tooltip')}
                enabled={!!hasApprovedInvoiceRequest}
              >
                <div data-testid="organization-field">
                  <Controller
                    control={control}
                    name="organizationId"
                    render={({ field, fieldState }) => (
                      <BreakoutSelect
                        disabled={
                          !!sectionCompleted || hasApprovedInvoiceRequest
                        }
                        allowClear
                        kind="secondary"
                        label={t('instructor_library.organization')}
                        options={organizationsToUse.map((org) => ({
                          label: org.data.organizationName,
                          value: org.id,
                        }))}
                        error={fieldState.error}
                        {...field}
                      />
                    )}
                  />
                </div>
              </BreakoutTooltip>
            )}

            {/* Spacer: */}
            <div />

            {showProductSelectionUI && (
              <PromotionSelectionWidget
                section={section}
                ref={promotionSelectionRef}
                showAdminFreeSectionOption={user.isCorre}
                sectionStartDate={sectionStartDate}
                disabled={!!sectionCompleted}
              />
            )}

            {showProductSelectionUI && (
              <Controller
                control={control}
                name="selectedProduct"
                render={({ field, fieldState }) => (
                  <div>
                    {productsLoading ? (
                      <div className="flex items-center justify-center">
                        <Spinner size={3} />
                      </div>
                    ) : (
                      <PricingSelectionWidget
                        customProducts={(customProductsToUse ?? []).map(
                          (product) => ({
                            productId: product.id,
                            productName: product.data.productName,
                            sectionPassPrice: product.data.sectionPassPrice,
                            sectionPassQuantity:
                              product.data.sectionPassQuantity,
                            isCustom: true,
                          })
                        )}
                        defaultProducts={(defaultProductsToUse ?? []).map(
                          (product) => ({
                            productId: product.id,
                            productName: product.data.productName ?? undefined,
                            sectionPassPrice: product.data.sectionPassPrice,
                            sectionPassQuantity:
                              product.data.sectionPassQuantity,
                            isCustom: false,
                          })
                        )}
                        legacyProductOption={legacyProductOption}
                        onSelectionChange={field.onChange}
                        selectedProduct={field.value}
                        disabled={!!sectionCompleted || !!pricingDisabled}
                        hasCorreOverride={hasCorreOverride}
                      />
                    )}
                    {fieldState.error && <FormError error={fieldState.error} />}
                  </div>
                )}
              />
            )}
            {isCompletedPayByAssignment && (
              <div>
                <div className="text-label-large">
                  {tt.instructor_class.pay_by_assignment()}
                </div>
              </div>
            )}
          </div>

          {error && (
            <FormError
              errorClass="mt-2"
              error={{
                type: 'error',
                message: error,
              }}
            />
          )}
        </div>
        <div className="flex justify-between gap-4">
          <BreakoutButton
            disabled={isSubmitting}
            size="medium"
            type="button"
            onClick={() => {
              onCancel ? onCancel() : clearAllDialogs()
            }}
            data-testid="cancel-button"
            kind="secondary"
          >
            {t('instructor_library.cancel')}
          </BreakoutButton>
          {!isReadOnly && (
            <BreakoutButton
              disabled={isSubmitting || productsLoading}
              size="medium"
              type="submit"
              kind="accent"
              loading={isSubmitting}
              data-testid="add-class-button"
            >
              {section?.id
                ? tt.instructor_class.save()
                : tt.instructor_library.add_class()}
            </BreakoutButton>
          )}
        </div>
      </form>

      {/* Section End Date Conflict Dialog */}
      <SectionEndDateConflictDialog
        open={showConflictDialog}
        onClose={handleConflictDialogClose}
        conflictingAssignments={conflictingAssignments}
        proposedEndDate={sectionEndDate}
        sectionId={section?.id || ''}
      />
    </>
  )
})

type SaveSection = (params: {
  className: string
  sectionName: string
  expectedStudents?: number
  organizationId?: string
  impersonationInfo?: ImpersonationInfo
  sectionStartDate?: DateTime
  sectionEndDate?: DateTime
  selectedProduct?: { productId: string; isCustom: boolean }
  isTest?: boolean
  sectionPaymentMethod?: SectionPaymentMethod
}) => Promise<string>

function SectionDatesWarningBox({
  watch,
}: {
  watch: UseFormWatch<FormValues>
}) {
  const { tt } = useTranslationTyped()
  const startDate = watch('sectionStartDate')
  const endDate = watch('sectionEndDate')

  if (startDate && endDate && endDate.diff(startDate, 'months').months <= 2) {
    return (
      <WarningBox className="mt-4">
        <strong className="text-body-medium">
          {tt.instructor_library.section_dates_warning_1()}
        </strong>
        <strong className="text-body-medium">
          {tt.instructor_library.section_dates_warning_2()}
        </strong>
      </WarningBox>
    )
  }

  return (
    <div className="flex items-center gap-6 rounded-[12px]">
      <div className="flex items-center gap-2">
        <TriangleAlertIcon
          size={16}
          className="shrink-0 text-fixed-accent-color"
        />
        <strong className="text-body-medium text-on-surface">
          {tt.instructor_library.section_dates_warning_3()}
        </strong>
      </div>
    </div>
  )
}
